import React from 'react';
import {
  FDBodyText, FDDialog, FDHeadlineText,
  FDIcon,
  FDDialogContent,
  FDStack, FDDialogActions, FDPrimaryActionButton, FDTitleText, FDBox,
} from '@facet-wealth/design';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { RoadmapExplorerGoalFundedProgress } from 'Components/UI/roadmapExplorer/goalsAccordion/goalFundedProgress';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { BorderedTransparentBox } from 'Packages/FacetDesign/Components/Experimental/BorderedTransparentBox';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';
import { GuidedHouseholdReviewZoneDescriptions } from 'Components/UI/guidedHouseholdReview/zoneDescriptions';

const DESKTOP_SMALL_DIALOG_WIDTH = 552;
const DIALOG_PADDING = 24;

export const RoadmapGoalItemDetailsModal = ({ open, onClose, showProgress }) => {
  const { resource } = useMetaLayoutContext();
  const isMobile = useMobileLayout();
  const theme = useTheme();
  const [currentWidth, setCurrentWidth] = React.useState(isMobile ? (window.innerWidth - (DIALOG_PADDING * 2)) : DESKTOP_SMALL_DIALOG_WIDTH);

  // The width of the goal funding component must be equal to the current width of the dialog (minus padding).
  // We are using preset values to determine the width because cannot use a ref - the opening of a dialog first
  // requires the layout animations to complete.
  React.useEffect(() => {
    const updateCurrentWidth = () => {
      setCurrentWidth(isMobile ? (window.innerWidth - (DIALOG_PADDING * 2)) : DESKTOP_SMALL_DIALOG_WIDTH);
    };
    window.addEventListener('resize', updateCurrentWidth);
    return () => {
      window.removeEventListener('resize', updateCurrentWidth);
    };
  }, [isMobile]);

  return (
    <FDDialog
      closeOnBackdropClick
      noCloseButton
      onClose={onClose}
      color={'subtle'}
      open={open}
      dialogWidth={'sm'}
      fullScreen={isMobile}
      {...dataAttrs('roadmap-goal-item-details-modal')}
    >
      <FDDialogContent>
        <FDStack direction={'column'} spacing={5} width={'100%'}>
          <FDStack direction={'column'} spacing={2}>
            <FDStack direction={'column'} spacing={1}>
              <FDIcon icon={resource.attr('icon')} size={'md'} />
              <FDHeadlineText value={resource.attr('name')} />
            </FDStack>
            <BorderedTransparentBox width={'100%'}>
              <FDStack direction={'row'} justifyContent={'space-between'}>
                <FDStack direction={'column'} paddingY={1} paddingX={2}>
                  <FDTitleText size={'sm'} value={resource.metaField('goalAmount').attr('prompt')} />
                  <FDTitleText size={'lg'} value={resource.attr('goalAmount')} />
                </FDStack>
                <Box sx={{ borderLeft: `1px solid ${theme.palette.facet.stone600}` }}>
                  <FDStack direction={'column'} paddingY={1} paddingX={2}>
                    <FDTitleText size={'sm'} value={resource.metaField('goalDate').attr('prompt')} />
                    <FDTitleText size={'lg'} value={resource.attr('goalDate')} />
                  </FDStack>
                </Box>
              </FDStack>
            </BorderedTransparentBox>
          </FDStack>
          <FDStack direction={'column'} spacing={1}>
            {!showProgress && <GuidedHouseholdReviewZoneDescriptions resource={resource} />}
            <RoadmapExplorerGoalFundedProgress
              resource={resource}
              width={currentWidth}
              progressDescription={showProgress ? resource.attr('progressDescription') : undefined}
              rightAlignedDescription={showProgress ? resource.attr('goalAmount') : undefined}
              progressValue={showProgress ? +resource.attr('progress') : 0}
              hasIcon={!showProgress}
            />
            <FDBox paddingX={1}>
              <Richtext unspaced value={resource.attr('details')} richtext Component={FDBodyText} size={'lg'} />
            </FDBox>
          </FDStack>
        </FDStack>
      </FDDialogContent>
      <FDDialogActions>
        <FDBox width={'100%'} paddingX={7}>
          <FDPrimaryActionButton
            label={'Close'}
            onClick={onClose}
            isFullWidth
          />
        </FDBox>
      </FDDialogActions>
    </FDDialog>
  );
};
